// src/pages/Schedule/AssignmentSchedule.jsx
import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import {
    FiHome, FiLayers, FiCalendar, FiChevronLeft, FiChevronRight,
    FiGrid, FiList, FiX, FiMove, FiHash, FiUser, FiTool, FiDollarSign,
    FiCheckCircle, FiClock, FiAlertCircle, FiPackage
} from "react-icons/fi";
import { TbTruckDelivery } from "react-icons/tb";
import { BrickWall, Grid2X2 } from "lucide-react";

import {
    DndContext,
    DragOverlay,
    useSensor,
    useSensors,
    PointerSensor,
    pointerWithin,
    MeasuringStrategy,
    useDndMonitor
} from '@dnd-kit/core';
import { useDraggable, useDroppable, defaultDropAnimationSideEffects } from '@dnd-kit/core';
import { snapCenterToCursor } from '@dnd-kit/modifiers';

import ConfirmationPopup from "../common/ConfirmationPopup";
import useDeviceDetection from "../common/useDeviceDetection";

import {
    getScheduleAssignments,
    getUnscheduledAssignments,
    getUnscheduledByQuote,
    updateFinishDate,
    updateJobSchedule
} from "../../lib/supabase/schedule";

const QuoteSchedule = () => {
    const { userProfile, company } = useAuth();
    const navigate = useNavigate();
    const { companySlug, quoteType } = useParams();
    const { isMobile } = useDeviceDetection();

    const [selectedWeek, setSelectedWeek] = useState("week1");
    const [scheduleData, setScheduleData] = useState({
        week1: {}, week2: {}, week3: {}, week4: {}
    });
    const [unscheduledAssignments, setUnscheduledAssignments] = useState([]);

    const [activeId, setActiveId] = useState(null);
    const [showConfirmation, setShowConfirmation] = useState(false);
    const [pendingMove, setPendingMove] = useState(null);

    // Mobile-specific states
    const [showMoveModal, setShowMoveModal] = useState(false);
    const [selectedAssignment, setSelectedAssignment] = useState(null);
    const [mobileViewMode, setMobileViewMode] = useState('grid');
    const [selectedDay, setSelectedDay] = useState(null);

    // Week switching states for drag and drop
    const [dragOverWeek, setDragOverWeek] = useState(null);
    const [switchProgress, setSwitchProgress] = useState(0);
    const [draggedItemCache, setDraggedItemCache] = useState(null);

    const [showOnlyMine, setShowOnlyMine] = useState(false);
    const [selectedUser, setSelectedUser] = useState('');
    const [availableUsers, setAvailableUsers] = useState({ designers: [], estimators: [] });

    const [isLoading, setIsLoading] = useState(false);

    const sensors = useSensors(
        useSensor(PointerSensor, {
            activationConstraint: {
                distance: 8,
            },
        })
    );

    const dropAnimationConfig = {
        duration: 200,
        easing: 'cubic-bezier(0.18, 0.67, 0.6, 1.22)',
        sideEffects: defaultDropAnimationSideEffects({
            styles: {
                active: {
                    opacity: '0.5',
                },
            },
        }),
    };

    useEffect(() => {
        return () => {
            setActiveId(null);
            setDragOverWeek(null);
            setSwitchProgress(0);
        };
    }, []);

    useEffect(() => {
        if (company?.id && quoteType) {
            fetchAssignments();
        }
    }, [company, quoteType, showOnlyMine, selectedUser]);

    // Fetch available users for filtering
    useEffect(() => {
        if (userProfile?.role === 'admin' || userProfile?.role === 'super_admin') {
            fetchAvailableUsers();
        }
    }, [userProfile]);

    const fetchAvailableUsers = async () => {
        try {
            // This would need a new query function to fetch users by role
            // For now, placeholder
            setAvailableUsers({ designers: [], estimators: [] });
        } catch (error) {
            console.error('Error fetching users:', error);
        }
    };

    const fetchAssignments = async () => {
        try {
            setIsLoading(true);

            const [scheduledRes, unscheduledRes] = await Promise.all([
                getScheduleAssignments(company.id, quoteType, {
                    showOnlyMine: showOnlyMine ? userProfile.id : null,
                    userId: selectedUser || null
                }),
                getUnscheduledByQuote(company.id, quoteType, {
                    showOnlyMine: showOnlyMine ? userProfile.id : null,
                    userId: selectedUser || null
                })
            ]);

            if (scheduledRes.error || unscheduledRes.error) {
                throw scheduledRes.error || unscheduledRes.error;
            }

            console.log(scheduledRes)

            setScheduleData(scheduledRes.data || {
                week1: {}, week2: {}, week3: {}, week4: {}
            });
            setUnscheduledAssignments(unscheduledRes.data || []);
        } catch (error) {
            console.error('Error fetching assignments:', error);
            // Set empty defaults on error
            setScheduleData({
                week1: {}, week2: {}, week3: {}, week4: {}
            });
            setUnscheduledAssignments([]);
        } finally {
            setIsLoading(false);
        }
    };

    // Helper functions
    const getAustralianDate = () => {
        return new Date(new Date().toLocaleString("en-US", { timeZone: "Australia/Melbourne" }));
    };

    const getWeekLabels = () => {
        const labels = [];
        const today = getAustralianDate();
        const monday = new Date(today);
        monday.setDate(today.getDate() - today.getDay() + 1);

        for (let i = 0; i < 4; i++) {
            const start = new Date(monday);
            start.setDate(monday.getDate() + i * 7);
            const end = new Date(start);
            end.setDate(start.getDate() + 6);

            const label = `${start.toLocaleDateString('en-AU', { day: '2-digit', month: 'short' })} – ${end.toLocaleDateString('en-AU', { day: '2-digit', month: 'short' })}`;
            labels.push(label);
        }

        return labels;
    };

    const getDayDate = (dayIndex) => {
        const today = getAustralianDate();
        const monday = new Date(today);
        monday.setDate(today.getDate() - today.getDay() + 1);

        const weekNumber = parseInt(selectedWeek.replace('week', '')) - 1;
        const targetDate = new Date(monday);
        targetDate.setDate(monday.getDate() + (weekNumber * 7) + dayIndex);

        return targetDate.toLocaleDateString('en-AU');
    };

    const getDayDateObject = (dayIndex) => {
        const today = getAustralianDate();
        const monday = new Date(today);
        monday.setDate(today.getDate() - today.getDay() + 1);

        const weekNumber = parseInt(selectedWeek.replace('week', '')) - 1;
        const targetDate = new Date(monday);
        targetDate.setDate(monday.getDate() + (weekNumber * 7) + dayIndex);

        const utcTargetDate = new Date(Date.UTC(
            targetDate.getFullYear(),
            targetDate.getMonth(),
            targetDate.getDate(),
            12, 0, 0, 0
        ));

        return utcTargetDate;
    };

    const days = [
        { label: "Mon", key: "Monday" },
        { label: "Tue", key: "Tuesday" },
        { label: "Wed", key: "Wednesday" },
        { label: "Thu", key: "Thursday" },
        { label: "Fri", key: "Friday" },
        { label: "Sat", key: "Saturday" },
    ];

    const getMainStatusColor = (status) => {
        switch (status) {
            case "pending_design": return 'bg-yellow-500';
            case "in_design": return 'bg-blue-500';
            case "design_completed": return 'bg-green-500';
            case "pending_estimation": return 'bg-orange-500';
            case "in_estimation": return 'bg-purple-500';
            case "estimation_completed": return 'bg-emerald-500';
            case "ready_for_delivery": return 'bg-teal-500';
            case "completed": return 'bg-gray-500';
            default: return 'bg-gray-400';
        }
    };

    const getQuoteIcon = (type) => {
        switch (type) {
            case 'roof': return <FiHome className="w-3 h-3" />;
            case 'wall': return <BrickWall className="w-3 h-3" />;
            case 'floor': return <Grid2X2 className="w-3 h-3" />;
            default: return <FiTool className="w-3 h-3" />;
        }
    };

    const todayLabel = getAustralianDate().toLocaleDateString("en-AU", {
        weekday: "short",
        timeZone: "Australia/Melbourne",
    });

    // Drag and Drop Handlers
    const handleDragStart = (event) => {
        setActiveId(event.active.id);

        const assignment = event.active.data.current?.assignment;
        if (assignment) {
            setDraggedItemCache(assignment);
        }
    };

    const handleDragEnd = (event) => {
        const { active, over } = event;

        setDragOverWeek(null);
        setSwitchProgress(0);
        const assignmentData = draggedItemCache || active.data.current?.assignment;

        setDraggedItemCache(null); // Clear cache

        if (!over) {
            setActiveId(null);
            return;
        }

        // Ignore drop on week selector buttons - they're just for navigation
        if (over.id.toString().startsWith('week-selector-')) {
            setActiveId(null);
            return;
        }

        // const assignmentData = active.data.current?.assignment;
        // if (!assignmentData) {
        //     setActiveId(null);
        //     return;
        // }

        if (over.id === 'unscheduled-zone') {
            setPendingMove({
                assignment: assignmentData,
                removeDate: true,
                quoteType: quoteType
            });
            setShowConfirmation(true);
        } else if (over.id.startsWith('day-')) {
            const [, weekStr, dayKey] = over.id.split('-');
            const dayIndex = days.findIndex(d => d.key === dayKey);

            if (dayIndex !== -1) {
                const targetDate = getDayDateObject(dayIndex);
                const newDate = new Date(targetDate);
                newDate.setUTCHours(10, 0, 0, 0);

                setPendingMove({
                    assignment: assignmentData,
                    newDate: newDate,
                    dayKey: dayKey,
                    targetWeek: weekStr,
                    quoteType: quoteType
                });

                setShowConfirmation(true);
            }
        }

        setActiveId(null);
    };

    const handleDragOver = (event) => {
        const { over } = event;
        // Could add visual feedback here if needed
    };

    const WeekSwitcher = () => {
        const timerRef = useRef(null);
        const progressRef = useRef(null);

        useDndMonitor({
            onDragMove: (event) => {
                const { over } = event;

                if (over?.id && over.id.toString().startsWith('week-selector-')) {
                    const weekIndex = parseInt(over.id.replace('week-selector-', ''));
                    const targetWeek = `week${weekIndex + 1}`;

                    if (targetWeek !== selectedWeek && dragOverWeek !== weekIndex) {
                        setDragOverWeek(weekIndex);
                        setSwitchProgress(0);

                        if (timerRef.current) clearTimeout(timerRef.current);
                        if (progressRef.current) clearInterval(progressRef.current);

                        progressRef.current = setInterval(() => {
                            setSwitchProgress(prev => {
                                if (prev >= 100) {
                                    clearInterval(progressRef.current);
                                    return 100;
                                }
                                return prev + 5;
                            });
                        }, 50);

                        timerRef.current = setTimeout(() => {
                            setSelectedWeek(targetWeek); // Now we CAN switch because item is cached
                            setDragOverWeek(null);
                            setSwitchProgress(0);
                            if (progressRef.current) clearInterval(progressRef.current);
                            timerRef.current = null;
                            progressRef.current = null;
                        }, 2000);
                    }
                } else if (!over?.id.startsWith('day-') && !over?.id.startsWith('unscheduled-zone')) {
                    if (timerRef.current) {
                        clearTimeout(timerRef.current);
                        timerRef.current = null;
                    }
                    if (progressRef.current) {
                        clearInterval(progressRef.current);
                        progressRef.current = null;
                    }
                    setDragOverWeek(null);
                    setSwitchProgress(0);
                }
            },
            onDragEnd: () => {
                if (timerRef.current) {
                    clearTimeout(timerRef.current);
                    timerRef.current = null;
                }
                if (progressRef.current) {
                    clearInterval(progressRef.current);
                    progressRef.current = null;
                }
                setDragOverWeek(null);
                setSwitchProgress(0);
            },
            onDragCancel: () => {
                if (timerRef.current) {
                    clearTimeout(timerRef.current);
                    timerRef.current = null;
                }
                if (progressRef.current) {
                    clearInterval(progressRef.current);
                    progressRef.current = null;
                }
                setDragOverWeek(null);
                setSwitchProgress(0);
            }
        });

        return null;
    };

    const DroppableWeekButton = ({ week, index, label }) => {
        const { setNodeRef } = useDroppable({
            id: `week-selector-${index}`,
            disabled: false, // Keep it enabled for drag detection
            data: {
                type: 'week-selector', // Mark this as a navigation element, not a drop target
            }
        });

        return (
            <button
                ref={setNodeRef}
                onClick={() => setSelectedWeek(`week${week}`)}
                className={`relative px-6 py-3 rounded-lg font-medium transition-all duration-200 overflow-hidden ${selectedWeek === `week${week}`
                    ? "bg-white text-blue-600 shadow-sm"
                    : dragOverWeek === index && switchProgress > 0
                        ? "bg-blue-500 text-white shadow-md transform scale-105"
                        : "text-gray-600 hover:text-gray-900 hover:bg-gray-50"
                    }`}
            >
                <div className="flex flex-col justify-center">
                    <span className="relative z-10 text-base">Week {week}</span>
                    <span className="relative z-10 text-blue-800/60 text-sm">{label}</span>
                </div>

                {dragOverWeek === index && switchProgress > 0 && (
                    <>
                        <div
                            className="absolute bottom-0 left-0 h-1 bg-white opacity-80 transition-all duration-100 ease-out z-20"
                            style={{ width: `${switchProgress}%` }}
                        />
                        <div className="absolute inset-0 flex items-center justify-center w-full h-full">
                            <span className="w-full h-full text-xs text-white font-semibold bg-blue-600 px-2 py-1 rounded z-10 flex items-center justify-center">
                                Switching...
                            </span>
                        </div>
                    </>
                )}
            </button>
        );
    };

    // Mobile handlers
    const handleMobileMove = (assignment, e) => {
        e.stopPropagation();
        setSelectedAssignment(assignment);
        setShowMoveModal(true);
    };

    const handleMobileMoveConfirm = (targetWeek, targetDay) => {
        const dayIndex = days.findIndex(day => day.key === targetDay);
        const targetDate = getDayDateObject(dayIndex);

        const newDate = new Date(targetDate);
        newDate.setUTCHours(10, 0, 0, 0);

        setPendingMove({
            assignment: selectedAssignment,
            newDate: newDate,
            dayKey: targetDay,
            targetWeek: targetWeek,
            quoteType: quoteType
        });

        setShowMoveModal(false);
        setShowConfirmation(true);
    };

    const handleConfirmMove = async () => {
        if (!pendingMove) return;

        try {
            setIsLoading(true);

            const { error } = await updateFinishDate(
                pendingMove.assignment.id,
                quoteType,
                pendingMove.removeDate ? null : pendingMove.newDate
            );

            console.log(pendingMove.assignment.id, quoteType, pendingMove.newDate)

            if (error) throw error;

            await fetchAssignments();
        } catch (error) {
            console.error("Failed to update assignment date:", error);
        } finally {
            setIsLoading(false);
            setShowConfirmation(false);
            setPendingMove(null);
            setActiveId(null);
        }
    };

    const handleCancelMove = () => {
        setShowConfirmation(false);
        setPendingMove(null);
        setActiveId(null);

        setDragOverWeek(null);
        setSwitchProgress(0);
        // if (weekSwitchTimer) {
        //     clearTimeout(weekSwitchTimer);
        //     setWeekSwitchTimer(null);
        // }
        // if (progressInterval) {
        //     clearInterval(progressInterval);
        //     setProgressInterval(null);
        // }
    };

    // Draggable Components
    const DraggableAssignmentCard = ({ assignment }) => {
        const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
            id: assignment.id, // Changed from assignment._id
            data: { assignment },
            disabled: userProfile?.role !== 'admin' && userProfile?.role !== 'super_admin',
        });

        return (
            <div
                ref={setNodeRef}
                {...attributes}
                {...listeners}
                className={`bg-white border-2 border-gray-200 rounded-lg p-3 shadow-sm hover:shadow-md transition-all ${isDragging ? 'opacity-50' : ''
                    } ${userProfile?.role === 'admin' || userProfile?.role === 'super_admin' ? 'cursor-grab active:cursor-grabbing' : 'cursor-pointer'}`}
                onClick={() => navigate(`/${companySlug}/assignment/${assignment.id}`)} // Changed from assignment._id
            >
                <div className={`w-full h-1 rounded-full mb-3 ${getMainStatusColor(assignment.main_status)}`}></div>

                <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-2 min-w-0 flex-1">
                        <FiHash className="w-3 h-3 text-gray-400 flex-shrink-0" />
                        <span className="font-semibold text-gray-900 text-sm truncate">
                            {assignment.unique_id}
                        </span>
                    </div>
                    <div className="flex items-center gap-2">
                        {(assignment.urgent_quote || assignment.urgent_design) && (
                            <span className="bg-red-100 text-red-700 text-xs font-bold px-2 py-1 rounded-full">
                                URGENT
                            </span>
                        )}
                        {isMobile && (userProfile?.role === 'admin' || userProfile?.role === 'super_admin') && (
                            <button
                                onClick={(e) => handleMobileMove(assignment, e)}
                                className="p-1 text-gray-400 hover:text-blue-600 transition-colors"
                            >
                                <FiMove className="w-4 h-4" />
                            </button>
                        )}
                    </div>
                </div>

                <div className="flex items-center gap-2 mb-3">
                    <FiUser className="w-3 h-3 text-blue-500 flex-shrink-0" />
                    <span className="text-sm text-gray-700 truncate font-medium">
                        {assignment.customer_name}
                    </span>
                </div>

                <div className="mb-3">
                    <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${assignment.job_type === 'design' ? 'bg-blue-100 text-blue-800' :
                        assignment.job_type === 'estimation' ? 'bg-purple-100 text-purple-800' :
                            'bg-green-100 text-green-800'
                        }`}>
                        {assignment.job_type === 'design' && <FiTool className="w-3 h-3 mr-1" />}
                        {assignment.job_type === 'estimation' && <FiDollarSign className="w-3 h-3 mr-1" />}
                        {assignment.job_type === 'design_estimation' && <FiTool className="w-3 h-3 mr-1" />}
                        {assignment.job_type?.replace('_', ' & ')}
                    </span>
                </div>

                <div className="flex items-center justify-between pt-2 border-t border-gray-100">
                    <div className="flex items-center gap-1">
                        <FiCalendar className="w-3 h-3 text-gray-400" />
                        <span className="text-xs text-gray-500">
                            {assignment.quote_info?.expected_finish_date
                                ? new Date(assignment.quote_info.expected_finish_date).toLocaleDateString('en-AU')
                                : 'No date'
                            }
                        </span>
                    </div>
                    <span className="text-xs text-gray-500 capitalize">
                        {assignment.main_status?.replace('_', ' ')}
                    </span>
                </div>
            </div>
        );
    };

    const DroppableDay = ({ weekKey, dayKey, dayIndex, children }) => {
        const { isOver, setNodeRef } = useDroppable({
            id: `day-${weekKey}-${dayKey}`,
        });

        return (
            <div
                ref={setNodeRef}
                className={`bg-gray-50 border-2 ${isOver ? 'border-blue-300 bg-blue-50' : 'border-gray-200'
                    } rounded-xl overflow-hidden hover:border-blue-300 transition-colors min-h-[300px]`}
            >
                <div className="bg-gradient-to-r from-gray-50 to-gray-100 px-3 py-2 border-b border-gray-200">
                    <div className="text-center">
                        <h3 className="font-bold text-gray-900 text-sm">{days[dayIndex].label}</h3>
                        <p className="text-xs text-gray-500">{getDayDate(dayIndex)}</p>
                        {dayKey.toLowerCase() === todayLabel.toLowerCase() && weekKey === "week1" && (
                            <span className="bg-green-100 text-green-800 text-xs font-medium px-2 py-1 rounded-full mt-1 inline-block">
                                Today
                            </span>
                        )}
                    </div>
                </div>
                <div className="p-2 space-y-2">
                    {children}
                </div>
            </div>
        );
    };

    const DroppableUnscheduled = ({ children }) => {
        const { isOver, setNodeRef } = useDroppable({
            id: 'unscheduled-zone',
        });

        return (
            <div
                ref={setNodeRef}
                className={`bg-white border-2 border-dashed ${isOver ? 'border-orange-300 bg-orange-50' : 'border-gray-300'
                    } rounded-xl p-6 hover:border-orange-300 transition-colors`}
            >
                {children}
            </div>
        );
    };

    // Mobile List View
    const MobileListView = () => (
        <div className="space-y-3">
            {days.map(({ label, key }, dayIndex) => {
                const dayAssignments = scheduleData[selectedWeek]?.[key] || [];
                return (
                    <div key={key} className="bg-white rounded-lg border border-gray-200 shadow-sm">
                        <div
                            className="px-4 py-3 bg-gray-50 rounded-t-lg cursor-pointer hover:bg-gray-100 transition-colors"
                            onClick={() => setSelectedDay(selectedDay === key ? null : key)}
                        >
                            <div className="flex items-center justify-between">
                                <div className="flex items-center gap-3">
                                    <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                                        <span className="font-bold text-blue-600 text-sm">{label}</span>
                                    </div>
                                    <div>
                                        <h3 className="font-semibold text-gray-900">{`${label}, ${getDayDate(dayIndex)}`}</h3>
                                        <p className="text-sm text-gray-500">{dayAssignments.length} assignment{dayAssignments.length !== 1 ? 's' : ''}</p>
                                    </div>
                                </div>
                                <div className="flex items-center gap-2">
                                    {label.toLowerCase() === todayLabel.toLowerCase() && selectedWeek === "week1" && (
                                        <span className="bg-green-100 text-green-800 text-xs font-medium px-2 py-1 rounded-full">
                                            Today
                                        </span>
                                    )}
                                    <FiChevronRight className={`w-4 h-4 text-gray-400 transition-transform ${selectedDay === key ? 'rotate-90' : ''
                                        }`} />
                                </div>
                            </div>
                        </div>

                        {selectedDay === key && (
                            <div className="p-4 border-t border-gray-100 space-y-3">
                                {dayAssignments.length > 0 ? dayAssignments.map((assignment) => (
                                    <DraggableAssignmentCard key={assignment.id} assignment={assignment} />
                                )) : (
                                    <div className="text-center py-6">
                                        <FiCalendar className="w-8 h-8 text-gray-300 mx-auto mb-2" />
                                        <p className="text-gray-500">No assignments scheduled</p>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                );
            })}
        </div>
    );

    const draggedAssignment = draggedItemCache || (activeId
        ? [...Object.values(scheduleData[selectedWeek] || {}).flat(), ...unscheduledAssignments]
            .find(a => a.id === activeId)
        : null);

    if (isLoading) {
        return (
            <div className="flex items-center justify-center min-h-screen">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                <span className="ml-3 text-gray-600">Loading schedule...</span>
            </div>
        );
    }

    return (
        <DndContext
            sensors={sensors}
            collisionDetection={pointerWithin}
            onDragStart={handleDragStart}
            onDragEnd={handleDragEnd}
            onDragOver={handleDragOver}
            measuring={{
                droppable: {
                    strategy: MeasuringStrategy.WhileDragging,
                },
            }}
        >
            <>
                {/* Header */}
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                    <div>
                        <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 flex items-center gap-2">
                            {getQuoteIcon(quoteType)}
                            {quoteType?.charAt(0).toUpperCase() + quoteType?.slice(1)} Schedule
                        </h1>
                        <p className="text-gray-600 mt-1">Manage and track construction job timelines</p>
                    </div>
                    <div className="flex items-center gap-3">
                        {isMobile && (
                            <div className="flex bg-gray-100 rounded-lg p-1">
                                <button
                                    onClick={() => setMobileViewMode('grid')}
                                    className={`p-2 rounded text-sm font-medium transition-colors ${mobileViewMode === 'grid' ? 'bg-white text-blue-600 shadow-sm' : 'text-gray-600'
                                        }`}
                                >
                                    <FiGrid className="w-4 h-4" />
                                </button>
                                <button
                                    onClick={() => setMobileViewMode('list')}
                                    className={`p-2 rounded text-sm font-medium transition-colors ${mobileViewMode === 'list' ? 'bg-white text-blue-600 shadow-sm' : 'text-gray-600'
                                        }`}
                                >
                                    <FiList className="w-4 h-4" />
                                </button>
                            </div>
                        )}
                        <div className="text-sm text-gray-500 bg-gray-50 px-3 py-2 rounded-lg">
                            Australian Eastern Time
                        </div>
                    </div>
                </div>

                {/* Filters */}
                <div className="space-y-4 border-b border-gray-200 pb-6">
                    <div className="flex flex-col sm:flex-row gap-4">
                        <div className="flex items-center">
                            <input
                                type="checkbox"
                                id="showOnlyMine"
                                checked={showOnlyMine}
                                onChange={(e) => setShowOnlyMine(e.target.checked)}
                                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                            />
                            <label htmlFor="showOnlyMine" className="ml-2 text-sm text-gray-700">
                                Show only my assignments
                            </label>
                        </div>

                        {(userProfile?.role === 'admin' || userProfile?.role === 'super_admin') && (
                            <div className="flex items-center gap-2">
                                <label className="text-sm text-gray-700">Filter by user:</label>
                                <select
                                    value={selectedUser}
                                    onChange={(e) => setSelectedUser(e.target.value)}
                                    className="border border-gray-300 rounded-md px-3 py-1 text-sm focus:ring-blue-500 focus:border-blue-500 min-w-[200px]"
                                >
                                    <option value="">All users</option>
                                    {availableUsers.designers?.length > 0 && (
                                        <optgroup label="Designers">
                                            {availableUsers.designers.map(user => (
                                                <option key={user.id} value={user.id}>
                                                    {user.username || user.full_name}
                                                </option>
                                            ))}
                                        </optgroup>
                                    )}
                                    {availableUsers.estimators?.length > 0 && (
                                        <optgroup label="Estimators">
                                            {availableUsers.estimators.map(user => (
                                                <option key={user.id} value={user.id}>
                                                    {user.username || user.full_name}
                                                </option>
                                            ))}
                                        </optgroup>
                                    )}
                                </select>
                            </div>
                        )}
                    </div>
                </div>

                {/* Week Navigation */}
                <div className="flex items-center justify-between">
                    <button
                        onClick={() => {
                            const currentWeek = parseInt(selectedWeek.replace('week', ''));
                            if (currentWeek > 1) setSelectedWeek(`week${currentWeek - 1}`);
                        }}
                        disabled={selectedWeek === 'week1'}
                        className="p-2 text-gray-400 hover:text-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
                    ><FiChevronLeft className="w-5 h-5" />
                    </button>

                    <div className="text-center">
                        <h2 className="font-semibold text-gray-900">
                            Week {selectedWeek.replace('week', '')}
                        </h2>
                        <p className="text-sm text-gray-500">
                            {getWeekLabels()[parseInt(selectedWeek.replace('week', '')) - 1]}
                        </p>
                    </div>

                    <button
                        onClick={() => {
                            const currentWeek = parseInt(selectedWeek.replace('week', ''));
                            if (currentWeek < 4) setSelectedWeek(`week${currentWeek + 1}`);
                        }}
                        disabled={selectedWeek === 'week4'}
                        className="p-2 text-gray-400 hover:text-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        <FiChevronRight className="w-5 h-5" />
                    </button>
                </div>

                {/* Week Selection Pills */}
                <div className="flex justify-center">
                    <WeekSwitcher />
                    {isMobile ? (
                        <div className="flex bg-gray-100 rounded-lg p-1">
                            {[1, 2, 3, 4].map((week) => (
                                <button
                                    key={week}
                                    onClick={() => setSelectedWeek(`week${week}`)}
                                    className={`px-4 py-2 rounded text-sm font-medium transition-colors ${selectedWeek === `week${week}`
                                        ? 'bg-white text-blue-600 shadow-sm'
                                        : 'text-gray-600 hover:text-gray-900'
                                        }`}
                                >
                                    {week}
                                </button>
                            ))}
                        </div>
                    ) : (
                        <div className="flex bg-gray-100 rounded-xl p-1 relative gap-2">
                            {[1, 2, 3, 4].map((week, index) => (
                                <DroppableWeekButton
                                    key={week}
                                    week={week}
                                    index={index}
                                    label={getWeekLabels()[index]}
                                />
                            ))}
                        </div>
                    )}
                </div>

                {/* Content - Grid or List View */}
                {isMobile && mobileViewMode === 'list' ? (
                    <MobileListView />
                ) : (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
                        {days.map(({ label, key }, dayIndex) => {
                            const dayAssignments = scheduleData[selectedWeek]?.[key] || [];
                            return (
                                <DroppableDay
                                    key={key}
                                    weekKey={selectedWeek}
                                    dayKey={key}
                                    dayIndex={dayIndex}
                                >
                                    {dayAssignments.length > 0 ? (
                                        dayAssignments.map((assignment) => (
                                            <DraggableAssignmentCard key={assignment.id} assignment={assignment} />
                                        ))
                                    ) : (
                                        <div className="text-center py-8">
                                            <FiCalendar className="w-6 h-6 text-gray-300 mx-auto mb-2" />
                                            <p className="text-xs text-gray-500">No assignments</p>
                                        </div>
                                    )}
                                </DroppableDay>
                            );
                        })}
                    </div>
                )}

                {/* Unscheduled Assignments Section */}
                <DroppableUnscheduled>
                    <div className="flex items-center justify-between mb-4">
                        <h3 className="text-lg font-semibold text-gray-900">Unscheduled Assignments</h3>
                        <div className="flex items-center gap-2">
                            <span className="bg-orange-100 text-orange-800 text-sm font-medium px-3 py-1 rounded-full">
                                {unscheduledAssignments?.length || 0} assignments
                            </span>
                            {!isMobile && (userProfile?.role === 'admin' || userProfile?.role === 'super_admin') && (
                                <span className="text-xs text-gray-500">Drop here to unschedule</span>
                            )}
                        </div>
                    </div>

                    {(!unscheduledAssignments || unscheduledAssignments.length === 0) ? (
                        <div className="text-center py-12 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
                            <FiPackage className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                            <p className="text-gray-500 font-medium">All assignments have been scheduled</p>
                            <p className="text-sm text-gray-400 mt-1">Great job!</p>
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                            {unscheduledAssignments.map((assignment) => (
                                <DraggableAssignmentCard key={assignment.id} assignment={assignment} />
                            ))}
                        </div>
                    )}
                </DroppableUnscheduled>

                {/* Status Legend */}
                <div className="bg-gray-50 rounded-lg p-4">
                    <h4 className="font-medium text-gray-900 mb-3">Status Legend</h4>
                    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 text-sm">
                        <div className="flex items-center gap-2">
                            <div className="w-3 h-3 rounded bg-yellow-500"></div>
                            <span>Pending Design</span>
                        </div>
                        <div className="flex items-center gap-2">
                            <div className="w-3 h-3 rounded bg-blue-500"></div>
                            <span>In Design</span>
                        </div>
                        <div className="flex items-center gap-2">
                            <div className="w-3 h-3 rounded bg-green-500"></div>
                            <span>Design Complete</span>
                        </div>
                        <div className="flex items-center gap-2">
                            <div className="w-3 h-3 rounded bg-orange-500"></div>
                            <span>Pending Estimation</span>
                        </div>
                        <div className="flex items-center gap-2">
                            <div className="w-3 h-3 rounded bg-purple-500"></div>
                            <span>In Estimation</span>
                        </div>
                        <div className="flex items-center gap-2">
                            <div className="w-3 h-3 rounded bg-emerald-500"></div>
                            <span>Estimation Complete</span>
                        </div>
                        <div className="flex items-center gap-2">
                            <div className="w-3 h-3 rounded bg-teal-500"></div>
                            <span>Ready for Delivery</span>
                        </div>
                        <div className="flex items-center gap-2">
                            <div className="w-3 h-3 rounded bg-gray-500"></div>
                            <span>Completed</span>
                        </div>
                    </div>
                </div>
            </>

            {/* Mobile Move Modal */}
            {showMoveModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-end sm:items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-t-xl sm:rounded-xl w-full max-w-md max-h-[80vh] overflow-hidden">
                        <div className="flex items-center justify-between p-4 border-b border-gray-200">
                            <h3 className="text-lg font-semibold text-gray-900">Move Assignment</h3>
                            <button
                                onClick={() => setShowMoveModal(false)}
                                className="p-2 text-gray-400 hover:text-gray-600"
                            >
                                <FiX className="w-5 h-5" />
                            </button>
                        </div>

                        <div className="p-4">
                            <div className="mb-4">
                                <div className="bg-gray-50 p-3 rounded-lg">
                                    <div className="flex items-center gap-2 mb-1">
                                        <FiHash className="w-4 h-4 text-gray-400" />
                                        <span className="font-medium">{selectedAssignment?.unique_id}</span>
                                    </div>
                                    <div className="flex items-center gap-2 text-sm text-gray-600">
                                        <FiUser className="w-3 h-3" />
                                        <span>{selectedAssignment?.customer_name}</span>
                                    </div>
                                </div>
                            </div>

                            <div className="space-y-4 max-h-60 overflow-y-auto">
                                {[1, 2, 3, 4].map((weekNum) => (
                                    <div key={weekNum}>
                                        <h4 className="font-medium text-gray-900 mb-2">
                                            Week {weekNum} - {getWeekLabels()[weekNum - 1]}
                                        </h4>
                                        <div className="grid grid-cols-3 gap-2">
                                            {days.map(({ label, key }) => (
                                                <button
                                                    key={`${weekNum}-${key}`}
                                                    onClick={() => handleMobileMoveConfirm(`week${weekNum}`, key)}
                                                    className="p-3 text-center border border-gray-200 rounded-lg hover:border-blue-300 hover:bg-blue-50 transition-colors"
                                                >
                                                    <div className="font-medium text-sm">{label}</div>
                                                    <div className="text-xs text-gray-500 mt-1">
                                                        {(() => {
                                                            const today = getAustralianDate();
                                                            const monday = new Date(today);
                                                            monday.setDate(today.getDate() - today.getDay() + 1);
                                                            const targetDate = new Date(monday);
                                                            targetDate.setDate(monday.getDate() + ((weekNum - 1) * 7) + days.findIndex(d => d.key === key));
                                                            return targetDate.toLocaleDateString('en-AU', { day: '2-digit', month: 'short' });
                                                        })()}
                                                    </div>
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Confirmation Popup */}
            <ConfirmationPopup
                isOpen={showConfirmation}
                title="Confirm Schedule Change"
                message={pendingMove
                    ? `Are you sure you want to reschedule "${pendingMove.assignment.unique_id}" to ${pendingMove?.newDate ? pendingMove.newDate.toLocaleDateString('en-AU') : "make Unscheduled"
                    }?`
                    : ""
                }
                confirmText="Reschedule Assignment"
                cancelText="Cancel"
                onConfirm={handleConfirmMove}
                onCancel={handleCancelMove}
            />

            {/* Drag Overlay */}
            <DragOverlay modifiers={[snapCenterToCursor]} dropAnimation={dropAnimationConfig}>
                {draggedAssignment ? (
                    <div
                        className="bg-white border-2 border-blue-500 rounded-lg p-3 shadow-2xl pointer-events-none"
                        style={{ opacity: 0.9 }}
                    >
                        <div className={`w-full h-1 rounded-full mb-3 ${getMainStatusColor(draggedAssignment.main_status)}`}></div>

                        <div className="flex items-start justify-between mb-3">
                            <div className="flex items-center gap-2 min-w-0 flex-1">
                                <FiHash className="w-3 h-3 text-gray-400 flex-shrink-0" />
                                <span className="font-semibold text-gray-900 text-sm truncate">
                                    {draggedAssignment.unique_id}
                                </span>
                            </div>
                            {(draggedAssignment.urgent_quote || draggedAssignment.urgent_design) && (
                                <span className="bg-red-100 text-red-700 text-xs font-bold px-2 py-1 rounded-full">
                                    URGENT
                                </span>
                            )}
                        </div>

                        <div className="flex items-center gap-2 mb-3">
                            <FiUser className="w-3 h-3 text-blue-500 flex-shrink-0" />
                            <span className="text-sm text-gray-700 truncate font-medium">
                                {draggedAssignment.customer_name}
                            </span>
                        </div>

                        <div className="mb-3">
                            <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${draggedAssignment.job_type === 'design' ? 'bg-blue-100 text-blue-800' :
                                draggedAssignment.job_type === 'estimation' ? 'bg-purple-100 text-purple-800' :
                                    'bg-green-100 text-green-800'
                                }`}>
                                {draggedAssignment.job_type === 'design' && <FiTool className="w-3 h-3 mr-1" />}
                                {draggedAssignment.job_type === 'estimation' && <FiDollarSign className="w-3 h-3 mr-1" />}
                                {draggedAssignment.job_type === 'design_estimation' && <FiTool className="w-3 h-3 mr-1" />}
                                {draggedAssignment.job_type?.replace('_', ' & ')}
                            </span>
                        </div>
                    </div>
                ) : null}
            </DragOverlay>
        </DndContext>

    );
};

export default QuoteSchedule;